import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';

import { AutoComplete } from 'primeng/autocomplete';
import { HisOption } from '../../genericsearch/model/his-option';
import { HisSelectWidget } from '../model/his-widget-select';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { HisSearchService } from '../../genericsearch/service/his-search.service';
import { debounceTime, Subject } from 'rxjs';
import { GenericSearchUtil } from '../utils/generic-search-util';


@Component( {
    selector: 'his-search-autocomplete',
    templateUrl: './his-search-autocomplete.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisSearchAutocompleteComponent implements OnInit, AfterViewInit {
    private uiService = inject(HisUiService);
    private searchService = inject( HisSearchService );

    private static readonly DEFAULT_LIMIT: number = 100;

    @Input() widget: HisSelectWidget;

    // minimum length of autocomplete input to trigger a search
    @Input() minLength = 1;
    // maximum number of suggestions to show in autocomplete
    @Input() limit: number = null;
    // show icon to open dropdown?
    @Input() showDropdownIcon = true;
    // show null item in autocomplete select list?
    @Input() showNullItem = false;

    @Input() showUnequalOption = false;

    // true if this is the first input element in the search mask
    @Input() isFirstInputElement = false;

    @Input() selectFrom: string;

    @Input() configFile: string;

    @ViewChild( 'inputElement' ) inputElement: AutoComplete;

    // suggestions for autocomplete
    filteredSelectList: HisOption[] = [];
    // previous autocomplete input string
    lastAutocompleteQuery: string = undefined;

    options: HisOption[];
    toolTip = '';

    currentAutocompleteQuery: Subject<string> = new Subject<string>();

    // true, if a selectFrom method call returned less results than the limit.
    // this means no subsequent rest calls are necessary
    hasLessResultsThanLimit: boolean = false;

    // emits the id of the selected element
    @Output() changed: EventEmitter<HisOption> = new EventEmitter();

    ngOnInit() {
        this.widget.options.subscribe( ( values: HisOption[] ) => {
            this.options = [];
            // avoid that the null item is added twice
            if ( values ) {

                // the label for the null item should be '', see #210958
                // the empty selection should always be available on the top of the options list, see #325731
                this.options.push( { id: null, label: '' } );

                if ( this.widget.showNullItem ) {
                    // (nicht gefüllt)/(value not existing) should be the second option of the options list, see #325731
                    let nullValueLabel = this.uiService.translate( 'genericSearch.nullValueMenuEntry' );
                    this.options.push( { id: 'ISNULL', label: nullValueLabel } );

                    console.log( 'widget.value', this.widget.value );
                    let option: HisOption = this.widget.value;
                    if (option && option.id === 'ISNULL') {
                        option.label = nullValueLabel;
                        this.widget.value = option;
                    }
                }

                this.options = this.options.concat( values );
                console.log( 'Obtained options for widget', this.widget.id, ':', this.options );
            }
        } );

        const debounceDelay: number = GenericSearchUtil.isLazySelectFrom( this.selectFrom, this.showDropdownIcon ) ? 200 : 0;

        this.currentAutocompleteQuery.pipe( debounceTime( debounceDelay ) ).subscribe( ( query ) => {
            this.filterSelectList( query );
        } );
    }

    ngAfterViewInit() {
        // Set focus on first input element
        if ( this.isFirstInputElement ) {
            setTimeout( () => this.inputElement.inputEL.nativeElement.focus() );
        }
    }

    getHtmlFocusElementId(): string {
        return this.widget.uid;
    }

    onUpdateInput( $event: { query: string } ) {
        let autocompleteQuery = $event.query;
        console.log( 'HisSearchAutocompleteComponent.onUpdateQuery(): event =', $event, ', query =', autocompleteQuery );
        if ( !this.showDropdownIcon && ( !autocompleteQuery || autocompleteQuery.length < this.minLength ) ) {
            return;
        }
        this.currentAutocompleteQuery.next( autocompleteQuery );
    }

    /**
     * Find dropdown suggestions. The event should only fire if the autocomplete input length >= minLength
     *
     * @param $event event containing the input field value
     */
    filterSelectList( autocompleteQuery: string ) {
        if ( GenericSearchUtil.isLazySelectFrom( this.selectFrom, this.showDropdownIcon ) || this.checkIfOptionsAreEmpty( this.options ) ) {
            this.filterOptionsByMethod( autocompleteQuery );
        } else {
            this.filterOptionsByList( autocompleteQuery );
        }
        this.lastAutocompleteQuery = autocompleteQuery;

    }

    private checkIfOptionsAreEmpty( options: HisOption[] ): boolean {
        if ( !options || options.length === 0 ) {
            return true;
        }

        const optionsList = options
            .slice()
            .filter( o => {
                const id = ( o?.id ?? null );
                if ( id === null || id === undefined ) {
                    return false;
                }
                const idStr = String( id ).trim().toUpperCase();

                return idStr !== 'ISNULL';
            } );

        return optionsList.length === 0;
    }


    private filterOptionsByList( autocompleteQuery: string ) {
        // Produce filteredSelectList:
        // * If the new query contains the previous one, then we filter the last filtering result again
        // * If the new query does not contain the last one (because some characters were removed from the
        //   autocomplete input field), then we filter 'options'
        // * Case is ignored.
        console.log( 'HisSearchAutocompleteComponent.filterOptionsByList(): query =', autocompleteQuery );
        console.log(
            'HisSearchAutocompleteComponent.filterSelectList(): Old filteredSelectList =',
            this.filteredSelectList, ', lastAutocompleteQuery =', this.lastAutocompleteQuery );
        let srcList = ( this.lastAutocompleteQuery && autocompleteQuery.indexOf( this.lastAutocompleteQuery ) > -1 )
            ? this.filteredSelectList : this.options;
        this.filteredSelectList = this.reduceFilteredOptions( srcList, autocompleteQuery );
        console.log(
            'HisSearchAutocompleteComponent.filterSelectList(): Filtered options on client, new filteredSelectList =',
            this.filteredSelectList );
    }

    private filterOptionsByMethod( autocompleteQuery: string ) {
        console.log( 'HisSearchAutocompleteComponent.filterOptionsByMethod(): query =', autocompleteQuery );
        console.log(
            'HisSearchAutocompleteComponent.filterOptionsByMethod(): Old filteredSelectList =',
            this.filteredSelectList, ', lastAutocompleteQuery =', this.lastAutocompleteQuery );

        const hasNarrowedQuery: boolean = this.lastAutocompleteQuery && autocompleteQuery.indexOf( this.lastAutocompleteQuery ) > -1;
        if ( hasNarrowedQuery && this.hasLessResultsThanLimit ) {
            this.filteredSelectList = this.reduceFilteredOptions( this.filteredSelectList, autocompleteQuery );
        } else {
            this.loadFilteredOptions( autocompleteQuery );
        }
    }

    private reduceFilteredOptions( srcList: HisOption[], query: string ): HisOption[] {
        let nextList: HisOption[] = [];
        let listElem: HisOption;
        for ( listElem of srcList ) {
            if ( listElem.label.toUpperCase().indexOf( query.toUpperCase() ) > -1 ) {
                nextList.push( listElem );
            }
        }
        return nextList;
    }

    private loadFilteredOptions( autocompleteQuery: string ) {
        const usedLimit = this.limit ? this.limit : HisSearchAutocompleteComponent.DEFAULT_LIMIT;
        this.searchService.loadFilteredSelectOptions( this.configFile, this.selectFrom, autocompleteQuery, usedLimit ).subscribe(
            ( result: HisOption[] ) => {
                if ( result ) {
                    this.filteredSelectList = result;
                    console.log( 'HisSearchAutocompleteComponent.loadFilteredOptions(): ' +
                        'filteredSelectList obtained from backend =', this.filteredSelectList );

                    this.hasLessResultsThanLimit = result.length < usedLimit;
                }
            }
        );
    }

    /**
     * The input field content has been deleted -> set model value to null.
     */
    onClear() {
        this.widget.value = null;
        this.emitSelectedElement();
    }

    /**
     * Pass selected element to wrapping component.
     */
    emitSelectedElement() {
        this.toolTip = this.widget.value?.label;
        this.changed.emit( this.widget.value );
    }
}
